import { FC } from 'react';
import { Modal, Alert } from '@mui/material';
import { CloseIcon } from 'assets';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { showSystemToast } from 'view/components/SystemToast';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useContextSelector as useCS } from 'use-context-selector';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { IndustryDropdownAutocomplete, LocationAutocomplete } from 'view/pages/profile/components';
import { CreateCompanyLogoUpload } from './CreateCompanyLogoUpload';

interface CreateCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (company: any) => void;
}

interface IFormInput {
  name: string;
  websiteUrl: string;
  emailDomain: string;
  descriptionShort: string;
  descriptionLong: string;
  industry: any;
  location: any;
  logo: any;
}

const CreateCompanyModalFields: FC<{
  onClose: () => void;
  isLoading: boolean;
  error: any;
}> = ({ onClose, isLoading, error }) => {
  const { watch, setValue } = useFormContext<IFormInput>();

  const nameValue = watch('name') || '';
  const industryValue = watch('industry');
  const locationValue = watch('location');

  return (
    <>
      <CS.ModalMainHeader style={{ marginBottom: '24px' }}>
        <CS.ModalMainHeaderTitle>Create New Company</CS.ModalMainHeaderTitle>
        <CS.ModalMainHeaderSubTitle>
          Add your company information to get started with posting jobs
        </CS.ModalMainHeaderSubTitle>
      </CS.ModalMainHeader>

      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '10px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.response?.data?.message || 'Failed to create company'}
          </Alert>
        )}

        <CreateCompanyLogoUpload
          onUpload={(id) => {
            return setValue('logo', { id });
          }}
          onRemove={() => {
            return setValue('logo', null);
          }}
        />

        <Input name="name" label="Company Name *" placeholder="e.g., Acme Inc." marginbottom={16} />

        <Input
          name="websiteUrl"
          label="Website URL *"
          placeholder="https://www.example.com"
          marginbottom={16}
        />

        <div style={{ marginBottom: '16px' }}>
          <IndustryDropdownAutocomplete industryValue={industryValue} setValue={setValue} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <LocationAutocomplete
            currentLocationValue={locationValue}
            setValue={(_name: any, val: any) => {
              return setValue('location', val);
            }}
          />
        </div>

        <Input
          name="emailDomain"
          label="Email Domain"
          subLabel="Enter the domain (without @) for work email verification"
          placeholder="example.com"
          marginbottom={16}
        />

        <Input
          name="descriptionShort"
          label="Short Description"
          placeholder="Brief summary of your company..."
          multiline
          rows={2}
          marginbottom={16}
        />

        <Input
          name="descriptionLong"
          label="Long Description"
          placeholder="Detailed company profile, culture, and values..."
          multiline
          rows={5}
          marginbottom={16}
        />

        <Alert severity="info" sx={{ mb: 3 }}>
          Your company will be pending admin approval. You can create draft job posts immediately,
          but publishing will require admin verification.
        </Alert>
      </div>

      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          onClick={onClose}
          disabled={isLoading}
          style={{ width: '100%', minHeight: '50px' }}
        />
        <Button
          variant="contained"
          text="Create Company"
          type="submit"
          disabled={isLoading || !nameValue.trim()}
          style={{ width: '100%', minHeight: '50px' }}
        />
      </CS.ModalMainFooter>
    </>
  );
};

export const CreateCompanyModal: FC<CreateCompanyModalProps> = ({ open, onClose, onCreated }) => {
  const userData: any = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const { send, isLoading, error } = useRequest({
    url: '/api/v1/companies',
    method: ApiMethodsEnum.Post,
  });
  const { send: fetchMemberships } = useRequest({
    url: '/api/v1/memberships/me',
    method: ApiMethodsEnum.Get,
  });

  // URL is provided per-call (send-verification needs the membership id) — see onSubmit.
  const { send: sendVerification } = useRequest({
    method: ApiMethodsEnum.Post,
  });

  const onSubmit = async (formData: IFormInput) => {
    // Validate websiteUrl if provided
    if (formData.websiteUrl && formData.websiteUrl.trim()) {
      try {
        const url = new URL(formData.websiteUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          showSystemToast('error', 'Website URL must use HTTP or HTTPS protocol');
          return;
        }
      } catch {
        showSystemToast('error', 'Please enter a valid website URL (e.g., https://example.com)');
        return;
      }
    }

    // Validate emailDomain if provided
    if (formData.emailDomain && formData.emailDomain.trim()) {
      const domainPattern =
        /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?)*\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(formData.emailDomain.trim())) {
        showSystemToast('error', 'Please enter a valid email domain (e.g., example.com)');
        return;
      }
    }

    // Transform data for backend
    const payload = {
      ...formData,
      industry: formData.industry ? { id: formData.industry.id } : null,
      location: formData.location ? { id: formData.location.id } : null,
      logo: formData.logo && formData.logo.id ? { id: formData.logo.id } : null,
    };

    const result = await send(payload);

    if (result) {
      // POST /companies auto-creates the creator's membership. Trigger work-email verification
      // for it with the user's account email via a separate send-verification call (§3).
      try {
        const memberships = await fetchMemberships();
        const membership = Array.isArray(memberships)
          ? memberships.find((m: any) => {
              return m.company?.id === result.id;
            })
          : null;
        if (membership?.id && userData?.email) {
          await sendVerification(
            { email: userData.email },
            `/api/v1/memberships/${membership.id}/send-verification`
          );
        }
      } catch {
        // Non-blocking: the company is created even if the verification email fails to send.
      }
      showSystemToast('success', 'Company created successfully. Pending admin approval.');
      onCreated(result);
      onClose();
    }
  };

  const defaultValues = {
    name: '',
    websiteUrl: '',
    emailDomain: '',
    descriptionShort: '',
    descriptionLong: '',
    industry: null,
    location: null,
    logo: null,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <CS.ModalMainWrapper style={{ width: '600px', maxWidth: '95%' }}>
        <CS.CloseIconBox onClick={onClose}>
          <CloseIcon />
        </CS.CloseIconBox>
        <FormContainer defaultValues={defaultValues} onSuccess={onSubmit}>
          <CreateCompanyModalFields onClose={onClose} isLoading={isLoading} error={error} />
        </FormContainer>
      </CS.ModalMainWrapper>
    </Modal>
  );
};
